import { Button, Group, MultiSelect, Paper, Select, TextInput } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { TrustScoreApi } from "../../apis";
import {
  ResolutionType, ReturnReason, ReturnRequestStatus, RequestType, TrustTier
} from "../../apis/return";
import { trustLevelLabel } from "./trust-level-badge";

// §9.1 date presets ("Огноо: 30 өдөр"). Numeric keys = trailing-N-day windows
// resolved in the page's buildParams; "custom" reveals the from/to pickers.
export type DatePreset = "7" | "30" | "90" | "all" | "custom";

const DATE_PRESET_VALUES: DatePreset[] = ["7", "30", "90", "all", "custom"];
const REQUEST_TYPE_VALUES: RequestType[] = ["SIMPLE", "RETURN"];
const RESOLUTION_VALUES: ResolutionType[] = ["REFUND_MONEY", "REPLACEMENT", "NEXT_ORDER_DISCOUNT", "NONE", "MIXED"];

export const REASON_OPTION_VALUES: ReturnReason[] = [
  "NOT_RECEIVED", "DELIVERY_DAMAGED", "WRONG_ITEM_DELIVERED", "CANCEL_BEFORE_SHIP",
  "DEFECTIVE", "QUALITY_ISSUE", "NOT_AS_DESCRIBED", "CHANGED_MIND", "EXPIRED",
  "CONSIGNMENT_UNSOLD", "WARRANTY_CLAIM", "RECALL", "PRICE_DISPUTE", "OTHER"
];

const TRUST_TIERS: TrustTier[] = ["EXCELLENT", "GOOD", "MODERATE", "AT_RISK", "HIGH_RISK"];

export interface ListFilterValue {
  search?: string;
  status?: ReturnRequestStatus[];
  requestType?: RequestType | null;
  returnReason?: ReturnReason[];
  resolutionType?: ResolutionType | null;
  buyerBusinessId?: string | null;
  trustTier?: TrustTier[];
  datePreset?: DatePreset;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  isConsignmentReturn?: boolean;
  isReceiveTimeRejection?: boolean;
}

interface Props {
  value: ListFilterValue;
  onChange: (next: ListFilterValue) => void;
  onClear: () => void;
  showSearch?: boolean;
}

export function ReturnListFilters ({ value, onChange, onClear, showSearch = true }: Props){
  const { t } = useTranslation("returns");
  // Buyer options — the seller's buyer pool from the trust-score directory
  // (typed, searchable). Covers buyers with a computed score; see report.
  const { data: buyerData } = useSWR(
    "returns.buyer-options",
    () => TrustScoreApi.list({ limit: 100 }),
    { revalidateOnFocus: false }
  );
  const buyerOptions = (buyerData?.items ?? []).map((b) => ({ value: b.businessId, label: b.businessName || "-" }));

  const datePresetOptions = DATE_PRESET_VALUES.map((v) => ({ value: v, label: t(`filters.datePreset.${v}`) }));
  const requestTypeOptions = REQUEST_TYPE_VALUES.map((v) => ({ value: v, label: t(`filters.requestType.${v}`) }));
  const resolutionOptions = RESOLUTION_VALUES.map((v) => ({ value: v, label: t(`filters.resolution.${v}`) }));
  const reasonOptions = REASON_OPTION_VALUES.map((v) => ({ value: v, label: t(`filters.reason.${v}`) }));
  const trustOptions = TRUST_TIERS.map((tier) => ({ value: tier, label: trustLevelLabel(tier) }));

  return (
    <Paper p="md" bg="gray.0" radius="md">
      <Group gap="sm" wrap="wrap" align="flex-end">
        {showSearch ? (
          <TextInput
            size="sm"
            label={t("filters.search")}
            placeholder={t("filters.searchPlaceholder")}
            leftSection={<MagnifyingGlassIcon size={16} />}
            value={value.search ?? ""}
            onChange={(e) => onChange({ ...value, search: e.currentTarget.value })}
            w={260}
          />
        ) : null}
        <Select
          size="sm"
          label={t("filters.date")}
          data={datePresetOptions}
          value={value.datePreset ?? "30"}
          onChange={(v) => onChange({ ...value, datePreset: (v as DatePreset) ?? "30" })}
          allowDeselect={false}
          w={170}
        />
        {value.datePreset === "custom" ? (
          <>
            <DatePickerInput
              size="sm"
              label={t("filters.start")}
              placeholder={t("filters.start")}
              value={value.dateFrom ?? null}
              onChange={(d) => onChange({ ...value, dateFrom: d as Date | null })}
              clearable
              w={160}
            />
            <DatePickerInput
              size="sm"
              label={t("filters.end")}
              placeholder={t("filters.end")}
              value={value.dateTo ?? null}
              onChange={(d) => onChange({ ...value, dateTo: d as Date | null })}
              clearable
              w={160}
            />
          </>
        ) : null}
        <Select
          size="sm"
          label={t("filters.buyer")}
          placeholder={t("filters.buyerPlaceholder")}
          data={buyerOptions}
          value={value.buyerBusinessId ?? null}
          onChange={(v) => onChange({ ...value, buyerBusinessId: v })}
          searchable
          clearable
          nothingFoundMessage={t("filters.notFound")}
          w={220}
        />
        <Select
          size="sm"
          label={t("filters.type")}
          placeholder={t("filters.anyPlaceholder")}
          data={requestTypeOptions}
          value={value.requestType ?? null}
          onChange={(v) => onChange({ ...value, requestType: v as RequestType | null })}
          clearable
          w={190}
        />
        <MultiSelect
          size="sm"
          label={t("filters.reasonLabel")}
          placeholder={t("filters.anyPlaceholder")}
          data={reasonOptions}
          value={value.returnReason ?? []}
          onChange={(v) => onChange({ ...value, returnReason: v as ReturnReason[] })}
          clearable
          w={240}
        />
        <Select
          size="sm"
          label={t("filters.resolutionLabel")}
          placeholder={t("filters.anyPlaceholder")}
          data={resolutionOptions}
          value={value.resolutionType ?? null}
          onChange={(v) => onChange({ ...value, resolutionType: v as ResolutionType | null })}
          clearable
          w={190}
        />
        <MultiSelect
          size="sm"
          label={t("listTable.col.trust")}
          placeholder={t("filters.trustPlaceholder")}
          data={trustOptions}
          value={value.trustTier ?? []}
          onChange={(v) => onChange({ ...value, trustTier: v as TrustTier[] })}
          clearable
          w={240}
        />
        <Button
          size="sm"
          variant="subtle"
          color="gray"
          leftSection={<XIcon size={16} />}
          onClick={onClear}
        >
          {t("filters.clear")}
        </Button>
      </Group>
    </Paper>
  );
}
